import { uploadTempFile, isValidFileType, getFileSizeMB } from './supabaseStorage'

const APIMART_BASE_URL = 'https://api.apimart.ai/v1'

const getApiKey = () => {
  return localStorage.getItem('apimart_api_key')
}

export const remixVideo = async ({ videoId, prompt, duration, aspectRatio, model }) => {
  const apiKey = getApiKey()

  if (!apiKey) {
    throw new Error('API key not set. Please configure your Apimart API key in settings.')
  }

  if (!videoId) {
    throw new Error('Video ID is required for remix operation.')
  }

  const modelMap = {
    'sora2': 'sora-2',
    'sora2-pro': 'sora-2-pro'
  }

  const mappedModel = modelMap[model] || 'sora-2'

  const requestBody = {
    model: mappedModel,
    prompt: prompt,
    duration: duration,
    aspect_ratio: aspectRatio === 'landscape' ? '16:9' : '9:16'
  }

  const endpoint = `${APIMART_BASE_URL}/videos/${videoId}/remix`

  console.log('=== VIDEO REMIX REQUEST ===')
  console.log('URL:', endpoint)
  console.log('Video ID:', videoId)
  console.log('Model:', mappedModel)
  console.log('Request Body:', JSON.stringify(requestBody, null, 2))

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify(requestBody)
    })

    console.log('Response Status:', response.status)

    if (!response.ok) {
      const errorText = await response.text()
      let errorData = {}
      try {
        errorData = JSON.parse(errorText)
      } catch (e) {
        errorData = { message: errorText }
      }

      console.error('Remix Error Response:', errorData)

      const errorMessage = errorData.error?.message || errorData.message || ''

      if (response.status === 404) {
        throw new Error('Original video not found. The video may have expired (24 hour limit) or the video ID is invalid.')
      }

      if (errorMessage.includes('无可用渠道') || errorMessage.includes('distributor') || response.status === 503) {
        throw new Error(
          'The Sora-2 remix service is currently unavailable.\n\n' +
          'This could be due to:\n' +
          '• Model not enabled in your Apimart account\n' +
          '• Temporary service outage\n' +
          '• Regional restrictions\n\n' +
          'Try again later or check Apimart dashboard.'
        )
      }

      throw new Error(errorMessage || `API Error: ${response.status} - ${response.statusText}`)
    }

    const data = await response.json()
    console.log('=== REMIX RESPONSE ===')
    console.log(JSON.stringify(data, null, 2))

    return data
  } catch (error) {
    console.error('=== REMIX ERROR ===')
    console.error('Error:', error)
    throw error
  }
}

export const generateVideo = async ({ prompt, duration, aspectRatio, model, image, metadata = {} }) => {
  const apiKey = getApiKey()

  if (!apiKey) {
    throw new Error('API key not set. Please configure your Apimart API key in settings.')
  }

  const modelMap = {
    'sora2': 'sora-2',
    'veo31': 'veo3.1-fast',
    'minimax': 'MiniMax-Hailuo-02'
  }

  const mappedModel = modelMap[model] || 'sora-2'

  const requestBody = {
    model: mappedModel,
    prompt: prompt,
    duration: duration,
    aspect_ratio: aspectRatio === 'landscape' ? '16:9' : '9:16'
  }

  // Handle image upload to Supabase
  if (image) {
    try {
      console.log('📤 Uploading image to Supabase Storage...')
      console.log('File size:', getFileSizeMB(image), 'MB')

      // Validate file type
      if (!isValidFileType(image, 'image')) {
        throw new Error('Invalid file type. Please upload a valid image (JPEG, PNG, WebP)')
      }

      // Upload to Supabase and get public URL
      const imageUrl = await uploadTempFile(image, 'image')
      
      console.log('✅ Image uploaded successfully!')
      console.log('Public URL:', imageUrl)

      // Use the Supabase URL instead of base64
      requestBody.image = imageUrl
    } catch (error) {
      console.error('❌ Failed to upload image:', error)
      throw new Error('Failed to upload image: ' + error.message)
    }
  }

  // For MiniMax, add metadata
  if (model === 'minimax') {
    requestBody.metadata = {
      resolution: metadata.resolution || '768p'
    }
  }

  const endpoint = `${APIMART_BASE_URL}/videos/generations`

  console.log('=== GENERATION REQUEST ===')
  console.log('Mode:', image ? 'IMAGE-TO-VIDEO' : 'TEXT-TO-VIDEO')
  console.log('URL:', endpoint)
  console.log('Model:', mappedModel)
  console.log('Has Image URL:', !!requestBody.image)
  console.log('Request Body:', JSON.stringify(requestBody, null, 2))

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${apiKey}`
      },
      body: JSON.stringify(requestBody)
    })

    console.log('Response Status:', response.status)

    if (!response.ok) {
      const errorText = await response.text()
      let errorData = {}
      try {
        errorData = JSON.parse(errorText)
      } catch (e) {
        errorData = { message: errorText }
      }

      console.error('Generation Error Response:', errorData)

      const errorMessage = errorData.error?.message || errorData.message || ''

      if (errorMessage.includes('无可用渠道') || errorMessage.includes('distributor') || response.status === 503) {
        const modelName = model === 'minimax' ? 'MiniMax-Hailuo-02' : model === 'veo31' ? 'Veo 3.1 Fast' : 'Sora 2'
        throw new Error(
          `The ${modelName} model is currently unavailable.\n\n` +
          `This could be due to:\n` +
          `• Model not enabled in your Apimart account\n` +
          `• Temporary service outage\n` +
          `• Regional restrictions\n\n` +
          `Try switching to another model or check Apimart dashboard.`
        )
      }

      throw new Error(errorMessage || `API Error: ${response.status} - ${response.statusText}`)
    }

    const data = await response.json()
    console.log('=== GENERATION RESPONSE ===')
    console.log(JSON.stringify(data, null, 2))

    return data
  } catch (error) {
    console.error('=== GENERATION ERROR ===')
    console.error('Error:', error)
    throw error
  }
}

export const checkVideoStatus = async (taskId) => {
  const apiKey = getApiKey()

  if (!apiKey) {
    throw new Error('API key not set. Please configure your Apimart API key in settings.')
  }

  const statusUrl = `${APIMART_BASE_URL}/tasks/${taskId}`

  try {
    const response = await fetch(statusUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorData = {}
      try {
        errorData = JSON.parse(errorText)
      } catch (e) {
        errorData = { message: errorText }
      }

      throw new Error(
        errorData.error?.message || errorData.message || `HTTP error! status: ${response.status}`
      )
    }

    const rawData = await response.json()

    let data = rawData
    if (rawData.data && !rawData.status && !rawData.id) {
      data = rawData.data
      if (Array.isArray(data) && data.length > 0) {
        data = data[0]
      }
    }

    const normalizedResponse = {
      id: data.id || data.task_id || taskId,
      status: data.status,
      progress: data.progress || 0,
      result: data.result || {},
      output: data.output,
      error: data.error,
      _raw: rawData
    }

    return normalizedResponse
  } catch (error) {
    console.error('=== STATUS CHECK ERROR ===')
    console.error('Error:', error)
    throw error
  }
}